import React, { useCallback, useState } from "react";
import {
    useInterruptableAction,
    UserLoginActionState,
    useUserStore,
} from "@infrastructure/Persistence/UserState";
import { EntityReactionType } from "@shared/Types/Reactions/ReactionTypes.types";
import {
    ReactionActionState,
    useReactionActionStore,
} from "../ReactionActionState/ReactionActionState";
import { ReactionActorConstructor } from "../ReactionActors";
import {
    ActionInProgress,
    UseReactOnEntityReturn,
} from "./useReactOnEntity.types";
import {
    ErrorState,
    useErrorStore,
} from "../../Modals/ServiceModals/GeneralErrorsModal/GlobalState";
import { ReactionAction, ReactionEntityName } from "../Reactions.types";
import { AxiosError } from "axios";
import {
    RESPONSE_CODE_RESOURCE_NOT_FOUND,
    RESPONSE_CODE_RESOURCE_SUSPENDED,
} from "@infrastructure/BackendClientCodes";
import { useAvatarCreationPrompt } from "../../AvatarCreationPrompt/AvatarCreationPromptStore";
import { logException } from "@infrastructure/logger";

export const SWITCHING_STATE_TO_IDLE_TIME = 300;

const getErrorStateForEntityType = (
    entityType: ReactionEntityName,
): ErrorState => {
    switch (entityType) {
        case "comment":
            return ErrorState.COMMENT_SUSPENDED;
        case "post":
            return ErrorState.POST_SUSPENDED;
    }
};

export const useReactOnEntity = (
    reactionActorConstructor: ReactionActorConstructor,
    setEntityReactionsState?: React.Dispatch<
        React.SetStateAction<EntityReactionType[]>
    >,
): UseReactOnEntityReturn => {
    const { setReactionActionState } = useReactionActionStore();
    const { setUserAuthContextState } = useUserStore();
    const [actionInProgressState, setActionInProgressState] =
        useState<ActionInProgress>();
    const { trigger: triggerAvatarCreationPrompt } = useAvatarCreationPrompt();
    const { setErrorState } = useErrorStore();

    const errorHandler = useCallback(
        (entityType: ReactionEntityName, error: unknown) => {
            if (error instanceof AxiosError) {
                if (
                    error.response?.status ===
                        RESPONSE_CODE_RESOURCE_SUSPENDED ||
                    error.response?.status === RESPONSE_CODE_RESOURCE_NOT_FOUND
                ) {
                    setErrorState(getErrorStateForEntityType(entityType));
                }
            }
            logException(error);
        },
        [setErrorState],
    );

    useInterruptableAction(
        actionInProgressState,
        async () => {
            const reactionActor =
                new actionInProgressState!.reactionActorConstructor(
                    actionInProgressState!.entityId,
                );

            try {
                const entityReactions = await reactionActor.performAction(
                    actionInProgressState!.reactionAction,
                    actionInProgressState!.reactionName,
                );
                setEntityReactionsState?.(entityReactions);
            } catch (e) {
                errorHandler(reactionActor.entityType, e);
            }
        },
        () => {
            setActionInProgressState(undefined);
        },
    );

    const reactOnEntityById = async (
        entityId: string,
        reactionName: string,
        reactionAction: ReactionAction,
    ) => {
        setReactionActionState(ReactionActionState.LOADING);
        setActionInProgressState({
            entityId,
            reactionActorConstructor,
            reactionAction,
            reactionName,
        });
        setUserAuthContextState(UserLoginActionState.CREATE_REACTION);

        const reactionActor = new reactionActorConstructor(entityId);

        try {
            const entityReactions = await reactionActor.performAction(
                reactionAction,
                reactionName,
            );
            triggerAvatarCreationPrompt();
            setEntityReactionsState?.(entityReactions);
            setActionInProgressState(undefined);
            setTimeout(() => {
                setReactionActionState(ReactionActionState.IDLE);
            }, SWITCHING_STATE_TO_IDLE_TIME);
        } catch (e) {
            setTimeout(() => {
                setReactionActionState(ReactionActionState.IDLE);
            }, SWITCHING_STATE_TO_IDLE_TIME);

            errorHandler(reactionActor.entityType, e);
        }
    };

    return {
        reactOnEntityById,
    };
};
